import { type z } from 'zod';
import {
  hasItemsSelected,
  type ItemId,
  type TaskConfig,
  type Tasks,
  type Workflow,
  type WorkflowData,
  type WorkflowItemData,
} from '../workflow/schemaTypes';
import { getEffectiveMode } from '../workflow/taskMode';
import { clientLogger } from './logger';
import type { TaskRuntimeContext } from '../workflow/types';

/**
 * Validates a specific item against a schema
 * @param itemData - the item's stored task data
 * @param schema - Zod schema to validate against
 * @param taskId - ID of task containing the schema
 * @returns boolean indicating if item passes validation
 *
 * Takes a non-null schema so the compiler keeps proving every caller decided what a
 * rules-free task means before getting here. A tolerant parameter would let a caller
 * forward `null` and silently inherit this function's answer, which is not always the
 * right one — the guard gate and the completeness check disagree about it.
 */
export const validateItemTaskData = (
  itemData: WorkflowItemData | undefined,
  schema: z.ZodType,
  taskId: Tasks,
) => {
  if (!itemData || !(taskId in itemData)) {
    return false;
  }

  return schema.safeParse(itemData[taskId as keyof WorkflowItemData]).success;
};

/**
 * How one guard stands. `unsatisfied` is a normal waiting state; `unresolvable` means the
 * guard names a task no step declares, which no shopper action can ever clear.
 */
export type GuardOutcome = 'satisfied' | 'unsatisfied' | 'unresolvable';

/**
 * Guard-drift reports already made, so a dead config is described once rather than on
 * every keystroke. Keyed by the guard name, which is what identifies the drift.
 */
const reportedUndeclaredGuards = new Set<Tasks>();

/**
 * How one guard stands — `satisfied` / `unsatisfied` / `unresolvable`, not a boolean, so
 * a caller cannot read the truthy `'unsatisfied'` as a yes.
 *
 * Deliberately separate from `isTaskComplete`, which asks whether a task still has
 * outstanding work. The two disagree in two places, so a shared helper would need a
 * caller-supplied flag for each:
 *
 * - **No rules.** Satisfied here regardless of data; `isTaskComplete` needs `data`
 *   present first, so it answers false before the session holds anything at all.
 * - **An item scope with no item map.** Unsatisfied here. `isTaskComplete` falls
 *   through to a cart-level read under the same id, which no code path writes for an
 *   item-scoped task, so it answers from an absent value.
 *
 * Guards resolve across every step, not just the current one, because a guard routinely
 * names a task on an earlier page — in the served flow the method task is alone on its
 * step, so any guard naming it is necessarily cross-step. A guard naming a task on a
 * *later* step is not resolved here at all: `resolveTaskGuards` answers `unresolvable`
 * for it before asking this, because its own page sits behind the Continue button the
 * outcome decides, so the step blocks rather than advancing past data it can never
 * collect.
 *
 * Only `disabled` is waved through, not every non-visible mode. A disabled task is
 * absent, so it has nothing to withhold. An `implicit` one is the opposite: it carries
 * real data that is validated without ever being rendered — the live tenant's cart is
 * exactly that — so it has to answer from its data like any other guard.
 */
export const resolveGuardOutcome = (
  guardTaskId: Tasks,
  data: WorkflowData | null,
  config: Workflow,
  itemId: ItemId | undefined,
  taskRuntimeContext?: TaskRuntimeContext,
): GuardOutcome => {
  const guardTask = config.steps
    .flatMap((step) => step.tasks)
    .find((task) => task.id === guardTaskId);

  if (!guardTask) {
    // Deduped for the same reason its forward-reference twin is: this sits on the
    // form-resolver path, which re-runs on every change, and `logError` has no rate
    // cap — so one drifted config would otherwise emit a Rollbar error per keystroke.
    if (!reportedUndeclaredGuards.has(guardTaskId)) {
      reportedUndeclaredGuards.add(guardTaskId);
      clientLogger.logError(
        new Error(`Guard names a task no step declares: ${guardTaskId}`),
        'Unresolvable task guard',
        { guardTaskId },
      );
    }
    return 'unresolvable';
  }

  const { schema, scope } = guardTask;
  if (!schema) {
    return 'satisfied';
  }

  if (getEffectiveMode(guardTask, taskRuntimeContext) === 'disabled') {
    return 'satisfied';
  }

  const satisfied = (met: boolean): GuardOutcome =>
    met ? 'satisfied' : 'unsatisfied';

  if (scope === 'cart') {
    return satisfied(
      schema.safeParse(data?.[guardTaskId as keyof WorkflowData]).success,
    );
  }

  if (!data?.items || !hasItemsSelected(data)) {
    return 'unsatisfied';
  }

  if (itemId) {
    return satisfied(
      validateItemTaskData(data.items[itemId], schema, guardTaskId),
    );
  }

  return satisfied(
    Object.keys(data.items).every((id) =>
      validateItemTaskData(data.items?.[id], schema, guardTaskId),
    ),
  );
};

const stepIndexOfTask = (config: Workflow, taskId: Tasks): number =>
  config.steps.findIndex((step) => step.tasks.some((t) => t.id === taskId));

/**
 * Forward references already reported, so a dead config is described once rather than on
 * every keystroke. Module-level because the resolver is called fresh per validation, and
 * keyed on the pair because one task can name several guards.
 *
 * A static property of the served config, so the set never needs clearing within a
 * session — the same pair reported twice would be the same defect.
 */
const reportedForwardReferences = new Set<string>();

/**
 * Both caches are module-global and append-only, which makes their lifetime invisible to
 * a caller. Tests currently pass only because each case picks an unused task/guard pair —
 * `vi.clearAllMocks()` resets the spy but cannot reach these — so a case that reused a
 * pair would be suppressed with nothing to point at. This is the seam that makes the
 * lifetime controllable, and the one production would clear on a config reload.
 */
export const resetGuardDriftReports = (): void => {
  reportedForwardReferences.clear();
  reportedUndeclaredGuards.clear();
};

/**
 * How a task's guards stand, collectively.
 *
 * Asked by both the renderer, deciding whether to draw the task, and the step validator,
 * deciding whether to judge it — so the "all of them" rule lives here rather than being
 * spelled out at each. A task with no guards is trivially satisfied.
 *
 * `unresolvable` outranks `unsatisfied` because the two want opposite handling. A guard
 * the shopper has not reached yet is a normal state and the task waits. A guard naming a
 * name no step declares is config drift that will never clear, and treating it as
 * "waiting" leaves the step passable with its data never collected.
 */
export const resolveTaskGuards = (
  task: TaskConfig,
  data: WorkflowData | null,
  config: Workflow,
  itemId: ItemId | undefined,
  taskRuntimeContext?: TaskRuntimeContext,
): GuardOutcome => {
  const guards = task.guards ?? [];
  if (guards.length === 0) return 'satisfied';

  // A guard the shopper has not reached yet is not "waiting" — it can never be
  // answered from here, because its own step is behind the Continue button this
  // outcome decides. Treated as unresolvable so the step blocks, rather than
  // passing with the guarded task's data never collected.
  //
  // Reported, because this is the outcome that hard-stops every shopper on the flow and
  // says nothing on screen: the task never renders, both gates disable Continue and
  // Submit, and the issue path is a task id with no registered field, so there is no
  // message to show. `processServerState` then pins the resume walk at this step for
  // every session. The sibling drift case below logs for less.
  //
  // Deduped rather than skipped: the earlier reasoning here was that the form-resolver
  // path runs per item on every change, but both gates ask this without an item and
  // short-circuit before their per-item loop, so it is once per validation — and the
  // seen-set makes it once per config either way.
  const taskStepIndex = stepIndexOfTask(config, task.id);
  const forwardGuardTaskId =
    taskStepIndex === -1
      ? undefined
      : guards.find((guardTaskId) => {
          const guardStepIndex = stepIndexOfTask(config, guardTaskId);
          return guardStepIndex !== -1 && guardStepIndex > taskStepIndex;
        });
  if (forwardGuardTaskId) {
    const seenKey = `${task.id}:${forwardGuardTaskId}`;
    if (!reportedForwardReferences.has(seenKey)) {
      reportedForwardReferences.add(seenKey);
      clientLogger.logError(
        new Error(
          `Guard is answered after the task it gates: ${task.id} is gated by ${forwardGuardTaskId}`,
        ),
        'Unresolvable task guard',
        { taskId: task.id, guardTaskId: forwardGuardTaskId },
      );
    }
    return 'unresolvable';
  }

  const outcomes = guards.map((guardTaskId) =>
    resolveGuardOutcome(guardTaskId, data, config, itemId, taskRuntimeContext),
  );

  if (outcomes.includes('unresolvable')) return 'unresolvable';
  if (outcomes.includes('unsatisfied')) return 'unsatisfied';
  return 'satisfied';
};

/**
 * Whether a task may be drawn. Both non-satisfied outcomes withhold it: there is nothing
 * useful to render for a guard that cannot be resolved either.
 */
export const areTaskGuardsSatisfied = (
  task: TaskConfig,
  data: WorkflowData | null,
  config: Workflow,
  itemId: ItemId | undefined,
  taskRuntimeContext?: TaskRuntimeContext,
): boolean =>
  resolveTaskGuards(task, data, config, itemId, taskRuntimeContext) ===
  'satisfied';

/**
 * Determines if a specific task is complete based on its validation requirements
 * @param taskId - ID of the task to check
 * @param data - Current workflow data
 * @param config - Workflow configuration
 * @param itemId - Optional specific item ID to check (for item-scoped tasks)
 * @returns boolean indicating if the task is complete
 */
export const isTaskComplete = (
  taskId: Tasks,
  data: WorkflowData | null,
  config: Workflow | null,
  itemId: ItemId | undefined,
) => {
  if (!config || !data) {
    return false;
  }

  // Find the task configuration from all steps
  const thisTask = config.steps
    .flatMap((step) => step.tasks)
    .find((task) => task.id === taskId);

  if (!thisTask) return false;

  const { schema, scope } = thisTask;

  // Nothing outstanding, so nothing to wait for. Sits below the `!data` guard, so it
  // answers false until the session holds any data at all — unlike the guard gate,
  // which answers before consulting data.
  if (!schema) {
    return true;
  }

  // For tasks that operate on individual items
  if (scope === 'item' && data.items) {
    // Task can't be complete if no items are selected
    if (!hasItemsSelected(data)) {
      return false;
    }

    // If specific item provided, validate just that item
    if (itemId) {
      return validateItemTaskData(data.items?.[itemId], schema, taskId);
    }

    // Otherwise validate all selected items
    return Object.keys(data.items || {}).every((itemId) =>
      validateItemTaskData(data.items?.[itemId], schema, taskId),
    );
  } else {
    // For tasks that operate on workflow-level data

    const taskData = data[taskId as keyof typeof data];
    return schema.safeParse(taskData).success;
  }
};
